import { useState, useCallback } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import clsx from 'clsx'
import Decimal from 'decimal.js'
import SortingIndicator from '@/components/SortingIndicator'
import TableListNoDataRow from '@/components/TableListNoDataRow'
import {
  marketMapState,
  markPriceMapState,
  marketSymbolState
} from '@/recoil/Market'
import {
  closePositionPopupState,
  positionInfoState,
  liquidationPriceMapValue
} from '@/recoil/Position'
import { reduceOrderMapValue } from '@/recoil/OrderList'
import { POSITION_LIST_HEADER_COLUMNS } from '@/constants/position'
import numberFormat from '@/utils/numberFormat'
import { calculateUnrealPnl } from '@/utils/futures'
import { alphabeticalSort, numericSort } from '@/utils/sorting'

import './PositionList.css'

function toggleSortingOrder(sorting) {
  return sorting === 'ascending' ? 'descending' : 'ascending'
}

function formatLiquidationPrice(price, markPrice, priceDecimal) {
  price = Decimal(price)
  markPrice = Decimal(markPrice)
  return price.lt(0) ||
    price.gt(markPrice.times(10)) ||
    price.lt(markPrice.times(0.01))
    ? '--'
    : numberFormat(price.toNumber(), priceDecimal)
}

export default function PositionList({ showAllMarketData }) {
  const [marketMap] = useRecoilState(marketMapState)
  const [markPriceMap] = useRecoilState(markPriceMapState)
  const [marketSymbol] = useRecoilState(marketSymbolState)
  const [positionInfo] = useRecoilState(positionInfoState)
  const liquidationPriceMap = useRecoilValue(liquidationPriceMapValue)
  const reduceOrderMap = useRecoilValue(reduceOrderMapValue)
  const setClosePositionPopup = useSetRecoilState(closePositionPopupState)
  const [sorting, setSorting] = useState({})
  const positionList = positionInfo.rows || []

  const updateSorting = useCallback(
    ({ key, defaultSortingOrder }) =>
      setSorting(prevSorting => {
        const sorting = { key }
        if (prevSorting.key === key) {
          if (prevSorting.order === defaultSortingOrder) {
            sorting.order = toggleSortingOrder(prevSorting.order)
          } else {
            return {}
          }
        } else {
          sorting.order = defaultSortingOrder
        }
        return sorting
      }),
    [setSorting]
  )

  const HeaderColumnList = POSITION_LIST_HEADER_COLUMNS.map(
    ({
      key,
      title,
      align = 'left',
      customClass = '',
      isSortable = true,
      defaultSortingOrder = 'descending'
    }) => (
      <th
        key={key}
        className={clsx('table-list-cell', `align-${align}`, customClass)}
      >
        {isSortable ? (
          <div
            className="sorting-label noselect"
            onClick={() => updateSorting({ key, defaultSortingOrder })}
          >
            {title}
            <SortingIndicator
              isActive={sorting.key === key}
              order={sorting.order}
            />
          </div>
        ) : (
          <>{title}</>
        )}
      </th>
    )
  )

  let filteredPositionList = (
    showAllMarketData
      ? positionList
      : positionList.filter(({ symbol }) => symbol === marketSymbol)
  ).map(position => {
    position = JSON.parse(JSON.stringify(position))
    const { symbol, position_qty, average_open_price } = position
    position.mark_price = markPriceMap[symbol] || position.mark_price
    position.liquidationPrice = liquidationPriceMap[symbol] || -1
    position.unrealPnl = calculateUnrealPnl(
      position_qty,
      position.mark_price,
      average_open_price
    )
    return position
  })

  filteredPositionList = sorting.key
    ? filteredPositionList.sort((a, b) => {
        const { key, order } = sorting
        const isNumericSort = [
          'position_qty',
          'average_open_price',
          'mark_price',
          'liquidationPrice',
          'unrealPnl'
        ].includes(key)
        const sortFunction = isNumericSort ? numericSort : alphabeticalSort
        const isAsc = order === 'ascending'
        return sortFunction(a[key], b[key], isAsc)
      })
    : filteredPositionList

  const PositionList = filteredPositionList.map(
    ({
      symbol,
      position_qty,
      average_open_price,
      mark_price,
      liquidationPrice,
      unrealPnl
    }) => {
      if (Decimal(position_qty).eq(0)) {
        return null
      }
      const [, baseCurrency] = symbol.split('_')
      const marketSymbol = `${baseCurrency}-PERP`
      const { priceDecimal = 0 } = marketMap[symbol] || {}
      const unrealPnlSign =
        unrealPnl === 0 ? '' : Decimal(unrealPnl).gt(0) ? '+' : '-'
      const { total: totalReduceOrder = 0, size: totalReduceOrderSize = 0 } =
        reduceOrderMap[symbol] || {}
      const hasReduceOrder = Decimal(totalReduceOrder).gt(0)
      const isFullyClose =
        hasReduceOrder &&
        Decimal(totalReduceOrderSize).sub(Decimal(position_qty).abs()).gte(0)
      const closePositionButtonTextPostfix = hasReduceOrder
        ? `(${totalReduceOrder})`
        : ''
      return (
        <tr key={symbol} className="table-list-row">
          <td className="table-list-padding-cell" />
          <td className="table-list-cell align-left">{marketSymbol}</td>
          <td
            className={clsx('table-list-cell align-left', {
              'color-positive': Decimal(position_qty).gt(0),
              'color-negative': Decimal(position_qty).lt(0)
            })}
          >
            {numberFormat(position_qty, {
              formatKey: 'max8',
              signDisplay: true
            })}
          </td>
          <td className="table-list-cell align-left">
            {numberFormat(average_open_price, priceDecimal)}
          </td>
          <td className="table-list-cell align-left">
            {numberFormat(mark_price, priceDecimal)}
          </td>
          <td className="table-list-cell align-left">
            {formatLiquidationPrice(liquidationPrice, mark_price, priceDecimal)}
          </td>
          <td
            className={clsx('table-list-cell align-left', {
              'color-positive': Decimal(unrealPnl).gt(0),
              'color-negative': Decimal(unrealPnl).lt(0)
            })}
          >
            {unrealPnlSign}
            {numberFormat(Decimal(unrealPnl).abs().toNumber(), 'max2')}
          </td>
          <td className="table-list-cell align-right action-column">
            <button
              className="cancel-order-button noselect"
              disabled={isFullyClose}
              onClick={() =>
                !isFullyClose &&
                setClosePositionPopup({
                  show: true,
                  marketSymbol: symbol
                })
              }
            >
              {`Close${closePositionButtonTextPostfix}`}
            </button>
          </td>
          <td className="table-list-padding-cell" />
        </tr>
      )
    }
  )

  const positionListNotEmpty = PositionList.filter(e => e).length > 0

  return (
    <div className="position-list-container">
      <table className="table-list">
        <thead>
          <tr className="table-list-header">
            <td className="table-list-padding-cell" />
            {HeaderColumnList}
            <td className="table-list-padding-cell" />
          </tr>
        </thead>
        <tbody>
          {positionListNotEmpty ? (
            PositionList
          ) : (
            <TableListNoDataRow
              addPaddingCell={true}
              displayText="No positions yet."
            />
          )}
        </tbody>
      </table>
    </div>
  )
}
